// src/context/CartContext.js

import React, { createContext, useContext } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage'; // Reutilizamos tu hook!
import { toast } from 'react-toastify';

const CartContext = createContext();

export function CartProvider({ children }) {
    const [cart, setCart] = useLocalStorage('cart', []);
    const [isInitialized, setIsInitialized] = React.useState(false);

    React.useEffect(() => {
        // Asegurar que el carrito esté inicializado correctamente
        if (cart !== undefined) {
            setIsInitialized(true);
        }
    }, [cart]);

    const addToCart = (itemToAdd) => {
        setCart(prevCart => {
            const existingItemIndex = prevCart.findIndex(item => 
                item.idProductoTalla === itemToAdd.idProductoTalla
            );

            const newCart = [...prevCart];

            if (existingItemIndex !== -1) {
                // El item ya existe, actualizamos la cantidad
                const existingItem = newCart[existingItemIndex];
                const newQuantity = existingItem.cantidad + itemToAdd.cantidad;

                if (newQuantity > existingItem.stockDisponible) {
                    toast.error('No puedes agregar más, excedes el stock disponible.', { autoClose: 2000 });
                    return prevCart; // No hacemos cambios
                }
                newCart[existingItemIndex].cantidad = newQuantity;
                toast.success(`Cantidad actualizada en el carrito`, { autoClose: 1000 });
            } else {
                // El item es nuevo, lo agregamos
                newCart.push(itemToAdd);
                toast.success(`${itemToAdd.cantidad} producto(s) agregado(s) al carrito`, { autoClose: 1000 });
            }
            
            return newCart;
        });
    };

    const removeFromCart = (idProductoTalla) => {
        setCart(prevCart => prevCart.filter(item => item.idProductoTalla !== idProductoTalla));
        toast.info('Producto eliminado del carrito', { autoClose: 1000 });
    };

    const updateQuantity = (idProductoTalla, newQuantity) => {
        setCart(prevCart => {
            return prevCart.map(item => {
                if (item.idProductoTalla === idProductoTalla) {
                    // Validamos que la nueva cantidad no exceda el stock
                    if (newQuantity > 0 && newQuantity <= item.stockDisponible) {
                        return { ...item, cantidad: newQuantity };
                    } else if (newQuantity > item.stockDisponible) {
                        toast.warn('Cantidad máxima de stock alcanzada', { autoClose: 1000 });
                        return { ...item, cantidad: item.stockDisponible };
                    }
                }
                return item;
            }).filter(item => item.cantidad > 0); // Opcional: eliminar si la cantidad llega a 0
        });
    };

    const clearCart = () => {
        setCart([]);
        toast.info('El carrito ha sido vaciado', { autoClose: 1000 });
    };

    // Calculamos valores derivados que estarán disponibles en toda la app
    const total = cart.reduce((acc, item) => acc + item.precio * item.cantidad, 0);
    const totalItems = cart.reduce((acc, item) => acc + item.cantidad, 0);

    const value = {
        cart: cart || [],
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        total,
        totalItems,
        isInitialized,
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
}

// Hook personalizado para consumir el contexto fácilmente
export const useCart = () => {
    const context = useContext(CartContext);
    if (context === undefined) {
        console.error('useCart debe ser usado dentro de un CartProvider');
        // Devolver un objeto con valores por defecto para evitar crashes
        return {
            cart: [],
            addToCart: () => {},
            removeFromCart: () => {},
            updateQuantity: () => {},
            clearCart: () => {},
            total: 0,
            totalItems: 0,
            isInitialized: false,
        };
    }
    return context;
};